// src/components/Insights/.../WeeklyBreakdown/Charts/ClientsAndPetsChart.tsx
import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import Select, { SelectOption } from 'components/UI/V3/Forms/Select';
import styles from '../WeeklyBreakdownCharts.module.scss';
import { Spinner } from 'components/UI';
import type { WeeklyBreakdownReport, WeekDayKey, WeekValues } from 'model/BookingsWeeklyBreakdownReport';

type Slice = { name: string; value: number; percentage: number; color: string };
type ViewKey = 'pets' | 'clients';

const COLORS = ['#BDD2CA', '#92B4A7', '#5E8677', '#BAA997', '#DCCCBB', '#EAB464', '#D5E1DB'];
const DAY_KEYS: WeekDayKey[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const VIEW_OPTIONS: Array<SelectOption<null>> = [
    { label: 'Total Pets', value: 'pets' },
    { label: 'Total Clients', value: 'clients' }
];

interface Props {
    loading: boolean;
    report: WeeklyBreakdownReport;
}

export const ClientsAndPetsChart: React.FC<Props> = ({ loading, report }) => {
    const [view, setView] = React.useState<ViewKey>('pets');

    const weekValues: WeekValues = React.useMemo(() => {
        return view === 'pets' ? report.totalPets : report.totalClients;
    }, [report, view]);

    const total = weekValues?.total ?? 0;

    const data: Slice[] = React.useMemo(() => {
        return DAY_KEYS.map((k, i) => {
            const dayLabel = report.days?.[k]?.format('ddd, [[]MM/DD/YYYY[]]') ?? `${k}`;
            const value = weekValues?.[k] ?? 0;
            const pct = total > 0 ? Number(((value * 100) / total).toFixed(2)) : 0;

            return {
                name: dayLabel,
                value,
                percentage: pct,
                color: COLORS[i % COLORS.length]
            };
        });
    }, [report, total, weekValues]);

    return (
        <div className={styles.card}>
            <div className={styles.headerRow}>
                <h3 className={styles.title}>Clients & Pets</h3>

                <Select
                    className={styles.inlineSelect}
                    options={VIEW_OPTIONS}
                    value={view}
                    onChange={val => setView(val as ViewKey)}
                    placeholder="Select view"
                />
            </div>

            <div className={styles.content}>
                {loading ? (
                    <div className={styles.loading}>
                        <Spinner className={styles.spinner} />
                    </div>
                ) : (
                    <>
                        <div className={styles.pieWrap}>
                            <div style={{ width: 186, height: 186 }}>
                                <ResponsiveContainer>
                                    <PieChart>
                                        <Pie
                                            data={data}
                                            dataKey="value"
                                            cx="50%"
                                            cy="50%"
                                            innerRadius="60%"
                                            outerRadius="80%"
                                            startAngle={90}
                                            endAngle={450}
                                            stroke="none"
                                        >
                                            {data.map((s, i) => (
                                                <Cell
                                                    key={i}
                                                    fill={s.color}
                                                />
                                            ))}
                                        </Pie>
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>

                            <div className={styles.centerLabel}>
                                <div className={styles.totalValue}>{total}</div>
                                <div className={styles.totalPercentage}>100%</div>
                            </div>
                        </div>

                        <div className={styles.legend}>
                            {data.map((s, i) => (
                                <div
                                    key={i}
                                    className={styles.legendItem}
                                >
                                    <div
                                        className={styles.legendDot}
                                        style={{ backgroundColor: s.color }}
                                    />
                                    <span className={styles.legendLabel}>{s.name}</span>
                                    <span className={styles.legendValue}>
                                        {s.value} ({s.percentage}%)
                                    </span>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ClientsAndPetsChart;
